// Табът „По дни" на панела на рецепта (досегашният модал „по дни"): колко е
// продадено на всеки ден С продажби и за колко (дните без продажби не се
// показват), с лента ∝ количеството спрямо най-силния ден (чист CSS, без
// графична библиотека); ред „Общо" и подсказка къде е разбивката по часове.
// Денят е по затварянето на сметката, както редът в справката.
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import type { SoldDaily } from "../../../../api/inventory";
import { formatMoney } from "../../components/numbers";
import type { Period } from "../../components/period/periodPresets";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
} from "../components/reportListModel";
import { formatCount } from "../components/soldReportModel";
import { buildDayRows, dayBarShares, formatDayWithWeekday, sumBy } from "./soldRecipeModel";

// Датата и лентата (до датата; в тесен панел — под нея) · продадено · стойност
// „ПРОДАДЕНО (БР)" се събира на един ред чак в 116px
const ROW_GRID = "grid grid-cols-[minmax(0,1fr)_116px_104px] items-center gap-x-2.5 px-4";

type Props = { daily: SoldDaily[] | undefined; isLoading: boolean; period: Period };

export const SoldRecipeDays = ({ daily, isLoading, period }: Props) => {
  const { t } = useTranslation();
  if (isLoading || !daily) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        {isLoading ? <Spin /> : <span className="text-[13px] text-body">{t("inventoryPage.errLoad")}</span>}
      </div>
    );
  }
  const rows = buildDayRows(period.from, period.to, daily);
  const barShares = dayBarShares(rows);

  return (
    <div className="@container flex min-h-0 flex-1 flex-col overflow-auto">
      <p className="m-0 border-b border-shade-2 px-4 py-2.5 text-[12.5px] leading-[1.4] text-body">
        {t("inventoryPage.sdDaysHint")}
      </p>
      <div className={`${ROW_GRID} ${REPORT_HEADER_ROW_CLASS} sticky top-0 z-[1] bg-panel`}>
        <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.colDate")}</span>
        <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.soldColSold")}</span>
        <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.colValue")}</span>
      </div>
      {rows.length === 0 && (
        <div className="px-4 py-8 text-center text-[13px] text-body">{t("inventoryPage.sdDaysEmpty")}</div>
      )}
      {rows.map((row, index) => (
        <div
          key={row.key}
          className={`${ROW_GRID} ${REPORT_ROW_CLASS} font-sheet tabular-nums text-ink-2`}
        >
          <span className="flex min-w-0 flex-col gap-1 @min-[420px]:flex-row @min-[420px]:items-center @min-[420px]:gap-3">
            <span className="shrink-0 whitespace-nowrap">{formatDayWithWeekday(row.day)}</span>
            <span
              aria-hidden
              className="block h-1.5 w-full min-w-0 overflow-hidden rounded-full bg-shade-1 @min-[420px]:h-2 @min-[420px]:flex-1"
            >
              {/* Дължината идва от данните — затова е inline style (изключение 1 на правило 3) */}
              <span className="block h-full rounded-full bg-brand" style={{ width: `${barShares[index] * 100}%` }} />
            </span>
          </span>
          <span className="text-right font-bold text-ink">{formatCount(row.quantity)}</span>
          <span className="text-right">{formatMoney(row.totalSum)}</span>
        </div>
      ))}
      <div className={`${ROW_GRID} ${REPORT_TOTAL_ROW_CLASS}`}>
        <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.total")}</span>
        <span className="text-right font-sheet font-bold tabular-nums text-ink">
          {formatCount(sumBy(rows, (row) => row.quantity))}
        </span>
        <span className="text-right font-sheet font-bold tabular-nums text-brand-deep-ink">
          {formatMoney(sumBy(rows, (row) => row.totalSum))}
        </span>
      </div>
      <p className="m-0 px-4 py-2.5 text-[12.5px] leading-[1.4] text-muted">{t("inventoryPage.sdDaysHoursHint")}</p>
    </div>
  );
};

export default SoldRecipeDays;
